import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getData, postData, putData, deleteData } from '../../services/apiService';
import Layout from '../../components/layout/Layout';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    IconButton, Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const AddQuizQuestion = () => {
    const [searchParams] = useSearchParams();
    const quizId = searchParams.get('quizId');

    // State management
    const [questions, setQuestions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const isFetched = useRef(false);
    const [currentQuestion, setCurrentQuestion] = useState({
        quizId: quizId,
        questionText: '',
        explanation: '',
        options: ['', ''],
        correctAnswer: [],
        questionType: 'single-choice'
    });
    const [errors, setErrors] = useState({});
    // Pagination and search states
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalPages, setTotalPages] = useState(1);

    // Fetch questions for the quiz
    const fetchQuestions = useCallback(async () => {
        try {
            const response = await getData(`/get-all-question/${quizId}?page=${page}&limit=${rowsPerPage}&search=${searchQuery}`);
            setQuestions(response.data);
            setTotalPages(response.totalPages);
        } catch (error) {
            toast.error('Failed to fetch questions');
            console.error(error);
        }
    }, [quizId, page, rowsPerPage, searchQuery]); // Add quizId as dependency since it's used in the function

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1); // Reset to first page on new search
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    useEffect(() => {
        if (!isFetched.current) {
            if (quizId) {
                fetchQuestions();
            }
        }
    }, [quizId, fetchQuestions, page, rowsPerPage, searchQuery]);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentQuestion(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle option changes
    const handleOptionChange = (index, value) => {
        const newOptions = [...currentQuestion.options];
        newOptions[index] = value;
        setCurrentQuestion(prev => ({
            ...prev,
            options: newOptions
        }));
    };

    // Add a new option field
    const addOption = () => {
        if (currentQuestion.options.length < 6) { // Limit to 6 options
            setCurrentQuestion(prev => ({
                ...prev,
                options: [...prev.options, '']
            }));
        }
    };

    // Remove an option
    const removeOption = (index) => {
        if (currentQuestion.options.length > 2) { // Minimum 2 options
            const newOptions = [...currentQuestion.options];
            newOptions.splice(index, 1);

            // Remove correct answer if it was the deleted option
            const newCorrectAnswer = currentQuestion.correctAnswer.filter(
                answer => answer !== currentQuestion.options[index]
            );

            setCurrentQuestion(prev => ({
                ...prev,
                options: newOptions,
                correctAnswer: newCorrectAnswer
            }));
        }
    };

    // Handle correct answer selection
    const handleCorrectAnswerChange = (option, isChecked) => {
        let newCorrectAnswer;

        if (currentQuestion.questionType === 'single-choice') {
            newCorrectAnswer = isChecked ? [option] : [];
        } else {
            newCorrectAnswer = isChecked
                ? [...currentQuestion.correctAnswer, option]
                : currentQuestion.correctAnswer.filter(a => a !== option);
        }

        setCurrentQuestion(prev => ({
            ...prev,
            correctAnswer: newCorrectAnswer
        }));
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!currentQuestion.questionText.trim()) {
            newErrors.questionText = 'Question text is required';
        }

        if (currentQuestion.options.some(opt => !opt.trim())) {
            newErrors.options = 'All options must be filled';
        }

        if (currentQuestion.options.length < 2) {
            newErrors.options = 'At least 2 options are required';
        }

        if (currentQuestion.correctAnswer.length === 0) {
            newErrors.correctAnswer = 'At least one correct answer is required';
        }

        if (currentQuestion.questionType === 'single-choice' && currentQuestion.correctAnswer.length > 1) {
            newErrors.correctAnswer = 'Single choice can have only one correct answer';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            if (isEditMode) {
                await putData(`/update-question/${currentQuestion._id}`, currentQuestion);
                toast.success('Question updated successfully');
            } else {
                await postData('/create-question', currentQuestion);
                toast.success('Question added successfully');
            }

            resetForm();
            fetchQuestions();
            setShowModal(false);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to save question');
            console.error(error);
        }
    };

    // Reset form to initial state
    const resetForm = () => {
        setCurrentQuestion({
            quizId: quizId,
            questionText: '',
            explanation: '',
            options: ['', ''],
            correctAnswer: [],
            questionType: 'single-choice'
        });
        setIsEditMode(false);
        setErrors({});
    };

    // Edit a question
    const editQuestion = (question) => {
        setCurrentQuestion({
            ...question,
            options: [...question.options], // Create a new array to avoid reference issues
            correctAnswer: [...question.correctAnswer]
        });
        setIsEditMode(true);
        setShowModal(true);
    };

    // Delete a question
    const deleteQuestion = async (id) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            try {
                await deleteData(`/delete-question/${id}`);
                toast.success('Question deleted successfully');
                fetchQuestions();
            } catch (error) {
                toast.error('Failed to delete question');
                console.error(error);
            }
        }
    };

    return (
        <Layout>
            <Box className="p-6 bg-gray-100 min-h-screen">
                <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Quiz Questions</Typography>
                {/* Header: Add Button (left) and Search (right) */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                    <Button variant="contained" sx={{
                        borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                        '&:hover': { backgroundColor: '#3A3897', },
                    }} onClick={() => setShowModal(true)} > + Add New Question </Button>
                    <TextField placeholder="Search Departments" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                        sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#757575' }} />
                                </InputAdornment>
                            ),
                            sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                        }}
                    />
                </Box>

                {/* Questions Table */}
                {questions.length > 0 ? (
                    <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} >
                        <Table striped bordered hover responsive>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Question </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Type </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Options </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Correct Answer(s) </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {questions.map((question, index) => (
                                    <TableRow key={question._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{question.questionText}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1"> {question.questionType.replace('-', ' ')}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1"><ul className="mb-0">
                                                {question.options.map((option, i) => (
                                                    <li key={i}>{option}</li>
                                                ))}
                                            </ul></Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{question.correctAnswer.join(', ')}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton color="primary" onClick={() => editQuestion(question)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => deleteQuestion(question._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <div className="alert alert-info">No questions found for this quiz.</div>
                )}
                {/* Pagination */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                '&.Mui-selected': {
                                    bgcolor: '#4B49AC',
                                    color: 'white',
                                },
                            },
                        }}
                    />
                    <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                        sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                        IconComponent={KeyboardArrowDownIcon} >
                        <MenuItem value={10}>10 / page</MenuItem>
                        <MenuItem value={20}>20 / page</MenuItem>
                        <MenuItem value={50}>50 / page</MenuItem>
                    </Select>
                </Box>

                {/* Add/Edit Question Modal */}
                <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" style={{ zIndex: 9999 }}>
                    <Modal.Header closeButton>
                        <Modal.Title>{isEditMode ? 'Edit Question' : 'Add New Question'}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Question Text: </Form.Label>
                                <Form.Control as="textarea" rows={3} name="questionText" value={currentQuestion.questionText} onChange={handleInputChange} isInvalid={!!errors.questionText} />
                                <Form.Control.Feedback type="invalid"> {errors.questionText} </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Question Type: </Form.Label>
                                <Form.Select name="questionType" value={currentQuestion.questionType} onChange={handleInputChange} >
                                    <option value="single-choice">Single Choice</option>
                                    <option value="multiple-choice">Multiple Choice</option>
                                    <option value="true-false">True/False</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <Form.Label>Options: </Form.Label>
                                    <Button variant="outline-primary" size="sm" onClick={addOption} disabled={currentQuestion.options.length >= 6}> Add Option </Button>
                                </div>

                                {currentQuestion.options.map((option, index) => (
                                    <div key={index} className="d-flex align-items-center mb-2">
                                        <Form.Check type={currentQuestion.questionType === 'multiple-choice' ? 'checkbox' : 'radio'} name="correctAnswer" checked={currentQuestion.correctAnswer.includes(option)}
                                            onChange={(e) => handleCorrectAnswerChange(option, e.target.checked)} className="me-2" disabled={!option.trim()} />
                                        <Form.Control value={option} onChange={(e) => handleOptionChange(index, e.target.value)} isInvalid={!!errors.options} />
                                        {currentQuestion.options.length > 2 && (
                                            <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => removeOption(index)} > Remove </Button>
                                        )}
                                    </div>
                                ))}

                                {errors.options && (<Form.Text className="text-danger">{errors.options}</Form.Text>)}
                                {errors.correctAnswer && (<Form.Text className="text-danger">{errors.correctAnswer}</Form.Text>)}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Answer Explanation: </Form.Label>
                                <Form.Control as="textarea" rows={3} name="explanation" value={currentQuestion.explanation} onChange={handleInputChange} isInvalid={!!errors.explanation} />
                                <Form.Control.Feedback type="invalid"> {errors.explanation} </Form.Control.Feedback>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => { setShowModal(false); resetForm(); }}> Close </Button>
                            <Button variant="contained" type="submit" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>{isEditMode ? 'Update Question' : 'Add Question'}</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </Box>
        </Layout>
    );
};

export default AddQuizQuestion;